/**
 * REGEN CLOCK — client-side countdowns for lazily-regenerating resources
 * (HP +5/min, nerve +1 per 2.5 min). The server never ticks; it hands us a
 * fractional anchor (`exact` at response time) plus the constant rate, and we
 * run the same straight line locally: no polling, one shared 1s heartbeat.
 */
import { ref, computed, watch, onUnmounted, type ComputedRef } from 'vue';

/** Server-provided anchor: fractional value at response time + rate. */
export interface RegenAnchor { exact: number; perMin: number }

export interface RegenClock {
  /** true once the resource sits at max (timers hidden). */
  full: boolean;
  /** seconds until the displayed integer ticks up */
  nextIn: number;
  /** seconds until the resource is back at max */
  fullIn: number;
}

// One heartbeat for every clock on screen — a timer per gauge is just waste.
const now = ref(Date.now());
let beat: number | undefined;
let listeners = 0;
function subscribe() {
  if (listeners++ === 0) beat = window.setInterval(() => { now.value = Date.now(); }, 1000);
}
function unsubscribe() {
  if (--listeners === 0) window.clearInterval(beat);
}

/** "1:23" under an hour, "1:02:03" past it. */
export function fmtEta(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
    : `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * Reactive countdown from a regen anchor. The getter re-runs whenever its
 * reactive sources change; the anchor is re-stamped to "now" only when its
 * VALUES change — a same-value re-emit must not reset the clock, or the
 * countdown would stall in place on every unrelated re-render.
 */
export function useRegenClock(
  getter: () => ({ anchor: RegenAnchor | null | undefined; max: number } | null),
): ComputedRef<RegenClock | null> {
  const stamped = ref<{ exact: number; perMin: number; max: number; at: number } | null>(null);

  watch(getter, (v) => {
    const a = v?.anchor;
    if (!a || !v || v.max <= 0 || a.perMin <= 0) { stamped.value = null; return; }
    const s = stamped.value;
    if (s && s.exact === a.exact && s.perMin === a.perMin && s.max === v.max) return;
    stamped.value = { exact: a.exact, perMin: a.perMin, max: v.max, at: Date.now() };
  }, { immediate: true, deep: true });

  subscribe();
  onUnmounted(unsubscribe);

  return computed<RegenClock | null>(() => {
    const s = stamped.value;
    if (!s) return null;
    const cur = Math.min(s.max, s.exact + s.perMin * ((now.value - s.at) / 60_000));
    if (cur >= s.max) return { full: true, nextIn: 0, fullIn: 0 };
    const nextIn = ((Math.floor(cur) + 1 - cur) * 60) / s.perMin;
    const fullIn = ((s.max - cur) * 60) / s.perMin;
    return { full: false, nextIn, fullIn };
  });
}
